import { HttpClient } from 'api/http-client';
import { ProjectId, Team, TeamWithMeta } from 'api/models';
import { EventSource, ProjectUserIdentifier } from 'api/project';

type CreateTeamPayload = {
  roleId?: number;
  teamType?: string;
  teamName: string;
};

type CreateProjectTeamPayload = CreateTeamPayload & {
  projectId: number;
  teamSource: EventSource;
  projectFinancialsBudgetIds?: number[];
};

export type StatusType = 'IN_PROGRESS' | 'FAILED' | 'SUBMITTED' | 'COMPLETED';

export type TeamsBulkUpdateStatus = {
  statusId?: number;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: number;
  status: StatusType;
  statusMeta?: {
    request: {
      teams: Array<{
        teamId: number;
        userList: Array<{
          userId: number;
          identityType: string;
        }>;
        projectId: number;
        identityType: string;
      }>;
    };
  };
  responseMeta?: {
    response: {
      response: {
        teams: Array<{
          team: {
            teamId: number;
            userList: Array<{
              userId: number;
              identityType: string;
            }>;
            projectId: number;
            identityType: string;
          };
          status: boolean;
        }>;
      };
    };
  };
  statusType?: string;
  requestId?: string;
};

export type AssignPlaceholderPayload = {
  teamId: number;
  teamName?: string;
  projectId: number;
  userList?: {
    userId: number;
  }[];
  companyList?: {
    companyId: number;
  }[];
  roleId?: number;
};

export type BulkAssignResponse = {
  teams: {
    team: {
      teamId: number;
      roleId?: number;
      userList: {
        userId: number;
      }[];
    };
  }[];
};

export interface TeamReplacementProps {
  projectId: number;
  teamId: number;
  userId: number;
  roleId: number;
}
export interface TeamReplacementWithCompanyProps {
  projectId: number;
  teamId: number;
  companyId: number;
  roleId: number;
}

export const OPERATION_TYPE = {
  ADD: 'ADD_PLACEHOLDER',
  REMOVE: 'REMOVE_PLACEHOLDER',
  UPDATE: 'UPDATE_PLACEHOLDER',
};

export interface PlaceholderBulkUpdate {
  addUserTeamList?: ProjectUserIdentifier[];
  removeUserTeamList?: ProjectUserIdentifier[];
  teams: AssignPlaceholderPayload[];
  operationType: string;
  assignedToProjectMember: boolean;
}

class TeamsHttpClient extends HttpClient {
  public async createTeam(payload: CreateTeamPayload): Promise<Team> {
    return this.post('/teams', payload);
  }

  public async createProjectTeam(
    payload: CreateProjectTeamPayload,
  ): Promise<Team> {
    return this.post('/teams', payload);
  }

  public async updateTeam(
    teamId: number,
    payload: Partial<CreateTeamPayload>,
  ): Promise<Team> {
    return this.put(`/teams/${teamId}`, payload);
  }

  public async deleteTeam(teamId: number): Promise<Team> {
    return this.delete(`/teams/${teamId}`);
  }

  public async deleteTeamsByRole(roleId: number): Promise<void> {
    return this.delete(`/teams/roles/${roleId}`);
  }

  public async bulkAssignPlaceholder(
    payload: AssignPlaceholderPayload[],
  ): Promise<BulkAssignResponse> {
    return this.put('/teams/bulk-action', payload);
  }

  public async bulkPlaceholdersUpdate(
    payload: PlaceholderBulkUpdate,
    projectId: number,
  ): Promise<BulkAssignResponse> {
    return this.post(`projects/${projectId}/bulk-update-placeholder`, payload);
  }

  public async assignPlaceholder(
    teamId: number,
    payload: Omit<AssignPlaceholderPayload, 'teamId'>,
  ): Promise<Team> {
    return this.put(`/teams/${teamId}`, payload);
  }

  public async getProjectPlaceholders(projectId: ProjectId): Promise<Team[]> {
    const [, _projectId] = projectId;

    return this.get(`/teams?projectIds=${_projectId}`);
  }

  public async getProjectPlaceholdersWithMeta(
    projectId: ProjectId,
  ): Promise<TeamWithMeta[]> {
    const [, _projectId] = projectId;

    return this.get(`/teams?projectIds=${_projectId}&meta=true`);
  }

  public async getAllTeamsInAccount(): Promise<Team[]> {
    return this.get('/teams?projectIds=all');
  }

  public async getTeamsBulkUpdateProgress(
    projectId: number,
  ): Promise<TeamsBulkUpdateStatus[]> {
    return this.get(`/async/status/projects/${projectId}/active`);
  }
}

export const TeamsAPI = new TeamsHttpClient();
